import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { isLocal } from "@/lib/offline/engine";
import { allCategories, catLabel } from "@/lib/lore";
import { useInvalidate } from "@/lib/queries";

export const entityText = (e) => [e?.summary || "", e?.content || "", ...Object.entries(e?.attributes || {}).map(([k, v]) => `${k}: ${v}`)].join("\n");

export async function requestDiscovery(entity, oldText, newText) {
  if (newText.trim() === oldText.trim() || isLocal(entity.universe_id)) return;
  try {
    const { data } = await api.post(`/entities/${entity.id}/ai/discover`, { old_text: oldText, new_text: newText });
    if (data.suggestions.length) window.dispatchEvent(new CustomEvent("lf-discover", { detail: { entity, suggestions: data.suggestions } }));
  } catch (e) {
    toast.error(errMsg(e));
  }
}

function SuggestionRow({ s, i, onChange }) {
  return (
    <div className={`rounded-md border p-4 transition-colors ${s.selected ? "border-[#D97706]/50 bg-[#1A202C]" : "border-[#2D3442] opacity-60"}`} data-testid={`discover-suggestion-${i}`}>
      <div className="flex items-center gap-2">
        <Checkbox checked={s.selected} onCheckedChange={(v) => onChange({ ...s, selected: !!v })} data-testid={`discover-select-${i}`} />
        <Input className={`h-9 font-display text-lg flex-1 ${s.selected ? "" : "line-through"}`} value={s.name} onChange={(e) => onChange({ ...s, name: e.target.value })} data-testid={`discover-name-${i}`} />
        <Select value={s.category} onValueChange={(v) => onChange({ ...s, category: v })}>
          <SelectTrigger className="h-9 w-40" data-testid={`discover-category-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent>{allCategories().map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Textarea rows={2} className="mt-2 text-sm" value={s.summary} onChange={(e) => onChange({ ...s, summary: e.target.value })} data-testid={`discover-summary-${i}`} />
    </div>
  );
}

export const DiscoverDialog = () => {
  const invalidate = useInvalidate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const on = (e) => setState({ entity: e.detail.entity, items: e.detail.suggestions.map((s) => ({ ...s, selected: true })) });
    window.addEventListener("lf-discover", on);
    return () => window.removeEventListener("lf-discover", on);
  }, []);
  if (!state) return null;
  const { entity, items } = state;
  const chosen = items.filter((s) => s.selected && s.name.trim());

  const create = async () => {
    setBusy(true);
    const ids = [];
    for (const s of chosen) {
      try {
        const { data } = await api.post(`/universes/${entity.universe_id}/entities`, {
          name: s.name.trim(), category: s.category, summary: s.summary, attributes: s.attributes || {},
          content: `First mentioned in ${entity.name}.`, related_entity_ids: [entity.id],
        });
        ids.push(data.id);
      } catch (e) {
        toast.error(`${s.name}: ${errMsg(e)}`);
      }
    }
    if (ids.length) {
      const { data: cur } = await api.get(`/entities/${entity.id}`);
      await api.patch(`/entities/${entity.id}`, { related_entity_ids: [...new Set([...(cur.related_entity_ids || []), ...ids])] });
      toast.success(`${ids.length} new ${ids.length === 1 ? "entry" : "entries"} added and linked to ${entity.name}`);
    }
    invalidate(["entities", entity.universe_id], "entity");
    setBusy(false);
    setState(null);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && setState(null)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto lf-scroll" data-testid="discover-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl flex items-center gap-2"><Sparkles className="h-6 w-6 text-[#F59E0B]" />New lore spotted</DialogTitle>
          <DialogDescription>Your edit to <b className="text-[#F3EBDD]">{entity.name}</b> mentions things that don't have entries yet. Create any you want, and they'll be linked back.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-4">
          {items.map((s, i) => <SuggestionRow key={i} s={s} i={i} onChange={(n) => setState({ ...state, items: items.map((x, j) => (j === i ? n : x)) })} />)}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setState(null)} disabled={busy} data-testid="discover-skip-button">Not now</Button>
          <Button onClick={create} disabled={busy || !chosen.length} data-testid="discover-create-button">{busy ? "Creating…" : `Create ${chosen.length} ${chosen.length === 1 ? "entry" : "entries"}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
